import { Button, ButtonProps } from "../ui/button"
import { HugeiconsIcon, Icons } from "../icons"

import { cn } from "@/lib/utils"
import { flushSync } from "react-dom"
import { useRef } from "react"
import { useTheme } from "@/hooks/use-theme"
import { useUserPreferences } from "@/hooks/use-user-preferences"

export function ThemeToggle(props?: ButtonProps) {
  const { theme, setTheme, skipNextTransitionRef } = useTheme()
  const { updatePreferences } = useUserPreferences(true)
  const themeButtonRef = useRef<HTMLButtonElement>(null)

  const toggleTheme = async () => {
    // Determine current effective theme (handle 'system' case)
    const getEffectiveTheme = (currentTheme: string) => {
      if (currentTheme === "system") {
        return window.matchMedia("(prefers-color-scheme: dark)").matches
          ? "dark"
          : "light"
      }
      return currentTheme
    }

    const currentEffectiveTheme = getEffectiveTheme(theme)
    const isDarkMode = currentEffectiveTheme === "dark"
    const newTheme = isDarkMode ? "light" : "dark"

    const applyNewTheme = () => {
      setTheme(newTheme)
      void updatePreferences({ theme: newTheme }).catch(() => {
        // Ignore persistence failure here; Settings page handles user-facing retry UX.
      })
    }

    /**
     * Return early if View Transition API is not supported
     * or user prefers reduced motion
     */
    if (
      !themeButtonRef.current ||
      !document.startViewTransition ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      applyNewTheme()
      return
    }

    try {
      // Pre-calculate values before starting transition to avoid layout shifts
      const buttonRect = themeButtonRef.current.getBoundingClientRect()
      const x = buttonRect.left + buttonRect.width / 2
      const y = buttonRect.top + buttonRect.height / 2
      const right = window.innerWidth - buttonRect.left
      const bottom = window.innerHeight - buttonRect.top
      const maxRadius = Math.hypot(
        Math.max(buttonRect.left, right),
        Math.max(buttonRect.top, bottom)
      )

      // Tell ThemeProvider not to run its own transition (we handle it here with custom animation)
      skipNextTransitionRef.current = true

      // Start transition immediately
      const transition = document.startViewTransition(() => {
        flushSync(() => {
          applyNewTheme()
        })
      })

      // Wait for transition to be ready
      await transition.ready

      // Animate the clip path with optimized settings for smooth performance
      // Use requestAnimationFrame to ensure browser is ready for animation
      requestAnimationFrame(() => {
        const animation = document.documentElement.animate(
          {
            clipPath: [
              `circle(0px at ${x}px ${y}px)`,
              `circle(${maxRadius}px at ${x}px ${y}px)`,
            ],
          },
          {
            duration: 500,
            easing: "cubic-bezier(0.4, 0, 0.2, 1)",
            pseudoElement: "::view-transition-new(root)",
            fill: "forwards",
          }
        )

        // Don't block - let animation run asynchronously
        animation.finished.catch(() => {
          // Ignore any errors
        })
      })
    } catch (error) {
      applyNewTheme()
    }
  }

  const effectiveTheme =
    theme === "system"
      ? window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light"
      : theme

  return (
    <Button
      {...props}
      ref={themeButtonRef}
      variant="ghost"
      size="icon"
      onClick={toggleTheme}
      className={cn(
        "text-primary-text size-fit hover:bg-transparent",
        props?.className
      )}
      title={
        effectiveTheme === "dark"
          ? "Switch to light mode"
          : "Switch to dark mode"
      }
    >
      {effectiveTheme === "dark" ? (
        <HugeiconsIcon icon={Icons.Sun02} size={20} />
      ) : (
        <HugeiconsIcon icon={Icons.Moon02} size={20} />
      )}
    </Button>
  )
}
